import { Children, Fragment, isValidElement, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';
import { microInteraction } from '../MicroInteractions';

/**
 * `select-none` prevents text-selection from horizontally scrolling the
 * overflow:hidden container, which would hide the text-overflow ellipsis
 * on user clicks. Matches the page-title typography for the `lg` default
 * (text-2xl); `sm` is the compact variant for non-header surfaces.
 */
const BASE_LABEL_CLASSES =
  'inline-block max-w-60 truncate text-sm align-middle font-medium tracking-tight select-none';

/** Ancestor links render muted to recede visually; current page keeps full foreground weight. */
function linkClasses(): string {
  return `${BASE_LABEL_CLASSES} text-muted-foreground ${microInteraction.breadcrumbLink}`;
}
function pageClasses(): string {
  return `${BASE_LABEL_CLASSES} text-foreground ${microInteraction.breadcrumbPage}`;
}

/**
 * Phase 6 — `Breadcrumbs` compound (Shadcn-canonical).
 *
 * Replaces the data-driven `BeatUIBreadcrumbs items={[…]}` API with a
 * dot-notation compound that exposes `ReactNode` slots:
 *
 * @example default (`lg`, text-2xl) — page header
 * ```tsx
 * <Breadcrumbs>
 *   <Breadcrumbs.Link to="/projects">Projects</Breadcrumbs.Link>
 *   <Breadcrumbs.Link to="/projects/123">My Project</Breadcrumbs.Link>
 *   <Breadcrumbs.Page>Asset name</Breadcrumbs.Page>
 * </Breadcrumbs>
 * ```
 *
 * @example compact (`sm`, text-sm) — secondary surfaces
 * ```tsx
 * <Breadcrumbs size="sm">
 *   <Breadcrumbs.Link to="/projects/123">My Project</Breadcrumbs.Link>
 *   <Breadcrumbs.Page>Asset name</Breadcrumbs.Page>
 * </Breadcrumbs>
 * ```
 *
 * @example custom separators
 * ```tsx
 * <Breadcrumbs manualSeparators>
 *   <Breadcrumbs.Link to="/projects">Projects</Breadcrumbs.Link>
 *   <Breadcrumbs.Separator><Slash className="size-sm" /></Breadcrumbs.Separator>
 *   <Breadcrumbs.Page>Asset</Breadcrumbs.Page>
 * </Breadcrumbs>
 * ```
 *
 * Reference compound pattern: `FeatureSidebar` in `shared/components/layout`.
 */

interface BreadcrumbsProps {
  children: ReactNode;
  /**
   * When true, opt out of auto-separator injection. Consumer is responsible
   * for placing `<Breadcrumbs.Separator>` between items. Useful when each
   * separator needs custom icons/markup.
   * @default false
   */
  manualSeparators?: boolean;
  /**
   * When set, collapse leading crumbs into an ellipsis whenever the total
   * count exceeds this value, leaving only the last `maxItems` visible.
   * Ignored when `manualSeparators` is true.
   */
  maxItems?: number;
}

interface BreadcrumbsLinkProps {
  children: ReactNode;
  to: string;
}

interface BreadcrumbsPageProps {
  children: ReactNode;
  /**
   * `BreadcrumbPage` (monks-ui) hardcodes `aria-disabled="true"` - correct for its usual case,
   * plain text naming the current page, which is never a link. Pass `true` when `children` is
   * itself interactive (a rename dropdown trigger, say): otherwise every control inside inherits
   * "disabled" in the accessibility tree from this ancestor, even though nothing about it is
   * actually disabled - assistive tech, and any accessibility-tree-based automation, will skip it.
   * @default false
   */
  interactive?: boolean;
}

interface BreadcrumbsSeparatorProps {
  /** Custom separator content (e.g. an icon). Defaults to v4's chevron. */
  children?: ReactNode;
}

function BreadcrumbsRoot({ children, manualSeparators = false, maxItems }: BreadcrumbsProps) {
  const childArray = Children.toArray(children).filter(isValidElement);

  // Drop leading crumbs once the total exceeds `maxItems`, keeping only the
  // last N so the user always sees their immediate context (current page +
  // nearest ancestors).
  const shouldTruncate = !manualSeparators && maxItems != null && childArray.length > maxItems;
  const visibleChildren = shouldTruncate
    ? childArray.slice(childArray.length - maxItems)
    : childArray;

  return (
    <TooltipProvider delayDuration={300}>
      <Breadcrumb>
        <BreadcrumbList className="flex-nowrap overflow-hidden">
          {manualSeparators ? (
            visibleChildren.map((child, index) => <Fragment key={index}>{child}</Fragment>)
          ) : (
            <>
              {shouldTruncate && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbEllipsis />
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={microInteraction.breadcrumbSeparator} />
                </>
              )}
              {visibleChildren.map((child, index) => {
                const isLast = index === visibleChildren.length - 1;
                return (
                  <Fragment key={index}>
                    {child}
                    {!isLast && (
                      <BreadcrumbSeparator className={microInteraction.breadcrumbSeparator} />
                    )}
                  </Fragment>
                );
              })}
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
    </TooltipProvider>
  );
}

function BreadcrumbsLink({ children, to }: BreadcrumbsLinkProps) {
  const { setNode, isTruncated } = useIsTruncated();

  const link = (
    <BreadcrumbLink asChild className={linkClasses()}>
      <Link to={to} ref={setNode}>
        {children}
      </Link>
    </BreadcrumbLink>
  );

  return (
    <BreadcrumbItem>
      {isTruncated ? (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent>{children}</TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </BreadcrumbItem>
  );
}

function BreadcrumbsPageComponent({ children, interactive = false }: BreadcrumbsPageProps) {
  const { setNode, isTruncated } = useIsTruncated();

  // `interactive` is only ever spread in when true: `BreadcrumbPage` (monks-ui) fills in
  // `aria-disabled="true"` via `Object.assign(defaults, props)`, so an `aria-disabled: undefined`
  // key present in `props` at all - even for the non-interactive default - would still win the
  // assign and erase that default.
  const page = (
    <BreadcrumbPage
      ref={setNode}
      tabIndex={-1}
      className={pageClasses()}
      {...(interactive ? { 'aria-disabled': false } : {})}
    >
      {children}
    </BreadcrumbPage>
  );

  return (
    <BreadcrumbItem>
      {isTruncated ? (
        <Tooltip>
          <TooltipTrigger asChild>{page}</TooltipTrigger>
          <TooltipContent>{children}</TooltipContent>
        </Tooltip>
      ) : (
        page
      )}
    </BreadcrumbItem>
  );
}

function BreadcrumbsSeparatorComponent({ children }: BreadcrumbsSeparatorProps) {
  return (
    <BreadcrumbSeparator className={microInteraction.breadcrumbSeparator}>
      {children}
    </BreadcrumbSeparator>
  );
}

BreadcrumbsLink.displayName = 'Breadcrumbs.Link';
BreadcrumbsPageComponent.displayName = 'Breadcrumbs.Page';
BreadcrumbsSeparatorComponent.displayName = 'Breadcrumbs.Separator';

export const Breadcrumbs = Object.assign(BreadcrumbsRoot, {
  Link: BreadcrumbsLink,
  Page: BreadcrumbsPageComponent,
  Separator: BreadcrumbsSeparatorComponent,
});
